import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import api from '../../../utils/api';
import { useToast, ToastHost } from '../../../components/portal/Toast';
import AttendanceCalendar from '../../../components/portal/AttendanceCalendar';
import AttendanceDayModal from '../../../components/portal/AttendanceDayModal';
import { anchorTracker, trackerElapsedMs } from '../../../utils/tracker-time';
import {
  RiPlayCircleLine, RiStopCircleLine, RiTaskLine,
  RiPlayFill,
} from 'react-icons/ri';

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function EmployeeAttendance() {
  const today = useMemo(() => new Date(), []);
  const { toast, showToast, clearToast } = useToast();

  // ── live tracker ─────────────────────────────────────────────────────────
  const [openTracker, setOpenTracker] = useState(null);
  const [taskInput, setTaskInput] = useState('');
  const [now, setNow] = useState(Date.now());
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!openTracker) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [openTracker]);

  const handleStopRef = useRef(() => {});

  const loadOpen = useCallback(() => {
    api.get('/attendance/open')
      .then((res) => {
        const d = res.data;
        const t = d?.tracker || d?.data || (d?.id ? d : null);
        setOpenTracker(anchorTracker(t));
      })
      .catch(() => {});
  }, []);

  // ── calendar month + entries ────────────────────────────────────────────
  const [calMonth, setCalMonth] = useState(today.getMonth() + 1);
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [entries, setEntries] = useState([]); // flat array
  const [loading, setLoading] = useState(true);

  // Selected day modal
  const [openDay, setOpenDay] = useState(null); // { dateStr, items }

  const loadMonth = useCallback(() => {
    setLoading(true);
    api.get('/attendance/mine', {
      params: { month: calMonth, year: calYear, limit: 1000 },
    })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.trackers || d?.data || [];
        // Server returns groups: [{ date, items: [...] }]. Flatten to a single array.
        const flat = list.flatMap((g) =>
          (g.items || g.trackers || g.entries || [g])
            .map((it) => ({ ...it, date: g.date || it.date }))
        );
        setEntries(flat);
      })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [calMonth, calYear]);

  useEffect(() => { loadOpen(); }, [loadOpen]);
  useEffect(() => { loadMonth(); }, [loadMonth]);

  // ── start/stop actions ──────────────────────────────────────────────────
  const handleStart = async (e) => {
    e?.preventDefault?.();
    const task = taskInput.trim();
    if (!task) return;
    setActionLoading(true);
    try {
      const res = await api.post('/attendance/start', { task });
      const t = res.data?.tracker || res.data?.data || res.data;
      setOpenTracker(anchorTracker(t));
      setTaskInput('');
      showToast('Tracker started.', 'success');
      loadMonth();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to start tracker.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      await api.post('/attendance/stop');
      setOpenTracker(null);
      showToast('Tracker stopped.', 'success');
      loadMonth();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to stop tracker.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => { handleStopRef.current = handleStop; });

  // Auto-stop at 00:00 IST (18:30 UTC). Mirrors the backend cron.
  useEffect(() => {
    if (!openTracker?.start_time) return;
    const CUTOFF_UTC_HOUR = 18;
    const CUTOFF_UTC_MIN = 30;
    const lastCutoff = (when) => {
      const d = new Date(when);
      const target = new Date(Date.UTC(
        d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
        CUTOFF_UTC_HOUR, CUTOFF_UTC_MIN,
      ));
      if (target > d) target.setUTCDate(target.getUTCDate() - 1);
      return target;
    };
    const start = new Date(openTracker.start_time);
    const check = () => {
      if (lastCutoff(new Date()) > start) handleStopRef.current?.();
    };
    check();
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [openTracker?.id, openTracker?.start_time]);

  const openElapsed = trackerElapsedMs(openTracker, now);

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">
      <ToastHost toast={toast} clearToast={clearToast} />

      {/* Header */}
      <div>
        <p className="label-meta mb-1">My Time</p>
        <h1 className="heading-md text-[#1F2937]">Attendance Tracker</h1>
        <p className="text-[#6B7280] text-sm mt-1">
          Start a tracker, switch tasks, and review your history on the calendar below. Click any day to see entries.
        </p>
      </div>

      {/* Live tracker card */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
        {openTracker ? (
          <div className="p-6" style={{ background: 'linear-gradient(135deg,rgba(9,132,227,0.04),rgba(213,0,249,0.04))' }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg,#0984e3,#d500f9)' }}>
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#0984e3]">Currently working on</p>
                  <p className="text-[#1F2937] text-base font-bold mt-0.5 truncate">{openTracker.task}</p>
                  <p className="text-[#6B7280] text-xs mt-1 flex items-center gap-1">
                    <RiPlayFill size={11} /> Started {fmtTime(openTracker.start_time)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">Elapsed</p>
                  <p className="text-[#0984e3] text-2xl font-black tabular-nums">{fmtDuration(openElapsed)}</p>
                </div>
                <button
                  onClick={handleStop}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer disabled:opacity-60"
                >
                  {actionLoading
                    ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <RiStopCircleLine size={16} />}
                  Stop
                </button>
              </div>
            </div>

            <form onSubmit={handleStart} className="mt-4 pt-4 border-t border-[#F3F4F6] flex items-center gap-2 flex-wrap">
              <RiTaskLine size={14} className="text-[#9CA3AF]" />
              <input
                type="text"
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                placeholder="Switch to a new task… (auto-closes the current one)"
                className="input-field flex-1 min-w-[220px] text-sm"
              />
              <button
                type="submit"
                disabled={actionLoading || !taskInput.trim()}
                className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ padding: '0.55rem 1rem', fontSize: '13px' }}
              >
                <RiPlayCircleLine size={14} /> Switch
              </button>
            </form>
          </div>
        ) : (
          <form onSubmit={handleStart} className="p-6 flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
              <RiTaskLine size={18} className="text-[#9CA3AF]" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">No tracker running</p>
              <p className="text-[#374151] text-sm font-semibold mt-0.5">Start a task to begin tracking time</p>
            </div>
            <input
              type="text"
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              placeholder="What are you working on?"
              className="input-field flex-1 min-w-[220px] text-sm"
            />
            <button
              type="submit"
              disabled={actionLoading || !taskInput.trim()}
              className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ padding: '0.55rem 1rem', fontSize: '13px' }}
            >
              {actionLoading
                ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <RiPlayCircleLine size={14} />}
              Start
            </button>
          </form>
        )}
      </div>

      {/* Calendar */}
      <AttendanceCalendar
        month={calMonth}
        year={calYear}
        onMonthChange={({ month, year }) => { setCalMonth(month); setCalYear(year); }}
        entries={entries}
        loading={loading}
        onDayClick={(dateStr, items) => setOpenDay({ dateStr, items })}
      />

      {/* Day-details modal — employees can rename their own task names */}
      {openDay && (
        <AttendanceDayModal
          dateStr={openDay.dateStr}
          items={openDay.items}
          onClose={() => setOpenDay(null)}
          editable
          onItemUpdated={({ id, task }) => {
            // Reflect the new task name in both:
            // 1. the modal-level items (so it shows immediately)
            // 2. the calendar-level entries (so the cell tooltip / next open reflects it)
            setOpenDay((prev) => prev && ({
              ...prev,
              items: prev.items.map((it) => (it.id || it._id) === id ? { ...it, task } : it),
            }));
            setEntries((prev) => prev.map((it) => (it.id || it._id) === id ? { ...it, task } : it));
            showToast('Task renamed.', 'success');
          }}
        />
      )}
    </div>
  );
}
